import React, { useLayoutEffect, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { cookiesConfig as defaultCookiesConfig } from '../config/cookiesConfig';
import { loadCookiesConfigForLocale } from '../config/configRegistry';
import { cookieManager } from '../utils/cookieManager';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { SITE_ORIGIN } from '../config/site';
import SiteHeader from './SiteHeader';
import Footer from './Footer';
import { setPrerenderReady } from '../utils/prerenderUtils';

const Cookies = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, ready } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [cookiesConfig, setCookiesConfig] = useState(defaultCookiesConfig);
  const [consentStatus, setConsentStatus] = useState({
    cookies: false,
    ads: false
  });

  // Load localized cookies config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await loadCookiesConfigForLocale(currentLanguage);
        setCookiesConfig(config);
      } catch (error) {
        console.error('Failed to load cookies config:', error);
        setCookiesConfig(defaultCookiesConfig);
      }
    };
    loadConfig();
  }, [currentLanguage]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    // Check current consent status
    const cookieConsent = cookieManager.get('cookieConsent') === 'true';
    const adsConsent = cookieManager.get('adsConsent') === 'true';
    setConsentStatus({ cookies: cookieConsent, ads: adsConsent });
  }, []);

  const handleWithdrawConsent = () => {
    if (window.withdrawConsent) {
      window.withdrawConsent();
      setConsentStatus({ cookies: false, ads: false });
    }
  };

  const handleAcceptAll = () => {
    cookieManager.set('cookieConsent', 'true');
    cookieManager.set('adsConsent', 'true');
    setConsentStatus({ cookies: true, ads: true });
    
    // Trigger consent banner to reload services
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cookieConsent',
      newValue: 'true'
    }));
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate(-1);
  };

  const createSectionId = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const renderSection = (section) => {
    const sectionId = createSectionId(section.title);
    
    return (
      <div className="prose max-w-none">
        <h2 
          id={sectionId}
          className="text-xl font-semibold text-gray-900 mb-4 scroll-mt-20"
        >
          {section.title}
        </h2>
        {section.subsections ? (
          <div className="space-y-6 pl-4">
            {section.subsections.map((subsection) => {
              const subsectionId = createSectionId(subsection.title);
              return (
                <div 
                  key={subsectionId}
                  id={subsectionId}
                  className="border-l-2 border-gray-100 pl-4 scroll-mt-20"
                >
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {subsection.title}
                  </h3>
                  <p className="text-gray-600">
                    {subsection.content}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600">
            {section.content}
          </p>
        )}
      </div>
    );
  };

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Set prerender ready when translations are loaded
  useEffect(() => {
    if (ready) {
      setPrerenderReady();
    }
  }, [ready]);

  // Show loading state until translations are ready
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <html lang={currentLanguage} />
        <title>{t('legal.cookies', 'Cookie Policy')}</title>
        <meta name="description" content={t('legal.cookiesDescription', "LiveLoveSpain's Cookies Policy - Learn how we use cookies and manage your cookie preferences for a better browsing experience.")} />
        <meta property="og:title" content={`${t('legal.cookies', 'Cookie Policy')} - LiveLoveSpain`} />
        <meta property="og:description" content={t('legal.cookiesDescription', "LiveLoveSpain's Cookies Policy - Learn how we use cookies and manage your cookie preferences for a better browsing experience.")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/cookies`} />
        <meta property="og:image" content={`${SITE_ORIGIN}/assets/LiveLoveSpain-512.png`} />
        <link rel="canonical" href={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/cookies`} />
        
        {/* Breadcrumb Schema */}
        <script key="ld:page" type="application/ld+json" data-page={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/cookies`}>
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [{
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": t('common.home', 'Home'),
                  "item": `${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}`
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": t('legal.cookies'),
                  "item": `${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/cookies`
                }
              ]
            }]}
          )}
        </script>
      </Helmet>
      
      {/* Header */}
      <SiteHeader />

      {/* Visual breadcrumb navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-blue-600 transition-colors">
              {t('common.home')}
            </Link>
          </li>
          <li>
            <span className="mx-2">/</span>
          </li>
          <li className="text-gray-900 font-medium">
            {t('legal.cookies')}
          </li>
        </ol>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('legal.cookiesPage.back')}
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {cookiesConfig.title}
            </h1>
            <p className="text-sm text-gray-500">
              {t('legal.cookiesPage.lastUpdated')} {cookiesConfig.lastUpdated}
            </p>
          </div>

          <p className="text-gray-600 mb-8">
            {cookiesConfig.intro}
          </p>

          {/* Consent Management Section */}
          <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('legal.cookiesPage.managePreferences')}</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-700 font-medium">{t('legal.cookiesPage.currentStatus')}</p>
                  <p className="text-sm text-gray-600">
                    Cookies: <span className={consentStatus.cookies ? 'text-green-600' : 'text-red-600'}>
                      {consentStatus.cookies ? t('legal.cookiesPage.cookiesAccepted') : t('legal.cookiesPage.cookiesDeclined')}
                    </span>
                    {' • '}
                    Ads: <span className={consentStatus.ads ? 'text-green-600' : 'text-red-600'}>
                      {consentStatus.ads ? t('legal.cookiesPage.adsAccepted') : t('legal.cookiesPage.adsDeclined')}
                    </span>
                  </p>
                </div>
                <div className="flex gap-3">
                  {!consentStatus.cookies && (
                    <button
                      onClick={handleAcceptAll}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {t('legal.cookiesPage.acceptAll')}
                    </button>
                  )}
                  {consentStatus.cookies && (
                    <button
                      onClick={handleWithdrawConsent}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      {t('legal.cookiesPage.withdrawConsent')}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {t('legal.cookiesPage.withdrawDescription')}
              </p>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">{t('legal.cookiesPage.tableOfContents')}</h2>
            <nav>
              <ul className="space-y-2">
                {cookiesConfig.sections.map((section) => {
                  const sectionId = createSectionId(section.title);
                  return (
                    <li key={sectionId}>
                      <a 
                        href={`#${sectionId}`}
                        onClick={(e) => scrollToSection(e, sectionId)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {section.title}
                      </a>
                      {section.subsections && (
                        <ul className="pl-4 mt-2 space-y-1">
                          {section.subsections.map((subsection) => {
                            const subsectionId = createSectionId(subsection.title);
                            return (
                              <li key={subsectionId}>
                                <a 
                                  href={`#${subsectionId}`}
                                  onClick={(e) => scrollToSection(e, subsectionId)}
                                  className="text-sm text-gray-600 hover:text-blue-600"
                                >
                                  {subsection.title}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {cookiesConfig.sections.map((section) => (
              <div key={createSectionId(section.title)}>
                {renderSection(section)}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Cookies; 